import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const posts = [
  {
    slug: "best-places-to-visit-in-trichy",
    title: "Best Places to Visit in Trichy",
    excerpt: "Discover the top attractions in Trichy — from the Rockfort Temple to Srirangam, Kallanai Dam, and more. Your complete guide to Trichy's must-visit spots.",
    date: "April 15, 2026",
    readTime: "8 min read",
    content: `Trichy (Tiruchirappalli), one of the oldest inhabited cities in Tamil Nadu, offers a unique blend of spiritual heritage and history. Whether you're a history buff, spiritual seeker, or food enthusiast, Trichy has something magical for everyone. Here's our guide to the best places to visit in Trichy.

**1. Rockfort Temple (Ucchi Pillayar Koil)**
The iconic Rockfort Temple, perched atop a 273-foot high rock, is the heart of Trichy. The climb of 437 steps offers breathtaking views of the city and the Cauvery river. The complex houses temples dedicated to Lord Ganesha and Lord Shiva.

**2. Sri Ranganathaswamy Temple, Srirangam**
Located on an island in the Cauvery river, Srirangam is the world's largest functioning Hindu temple. Its 21 grand gopurams (towers), including the massive Rajagopuram, are architectural marvels. It's a key pilgrimage site for Vaishnavites.

**3. Jambukeswarar Temple, Thiruvanaikoil**
One of the five major Shiva temples representing the element of water (Appu Lingam). The temple complex is known for its beautiful stone carvings and peaceful atmosphere.

**4. Kallanai Dam (Grand Anicut)**
Built across the Cauvery river by King Karikala Chola in the 2nd century AD, this is one of the oldest water-diversion structures in the world still in use. It's a testament to the engineering brilliance of ancient India.

**5. St. Lourdes Church**
Located near the Rockfort, this stunning Gothic church features beautiful architecture and peaceful interiors. It's a fine example of the cross-cultural heritage of the city.

**Getting Around Trichy**
The best way to explore all these attractions is with a reliable taxi service. Our Trichy to Velankanni Taxi offers flexible local tour packages. Book your Trichy sightseeing taxi today!`,
  },
  {
    slug: "trichy-travel-guide",
    title: "Trichy Travel Guide 2026",
    excerpt: "Everything you need to know about traveling to Trichy — best time to visit, how to reach, where to stay, and essential travel tips.",
    date: "April 10, 2026",
    readTime: "10 min read",
    content: `Planning a trip to Trichy? This comprehensive travel guide covers everything from the best time to visit to getting around, and local insights.

**Best Time to Visit Trichy**
The ideal time to visit is from October to March when the weather is pleasant. Avoid the peak summer months (April-June) as it can get quite hot.

**How to Reach Trichy**
By Air: Trichy International Airport (TRZ) connects to major Indian cities and international destinations like Singapore, Dubai, and Sri Lanka.
By Train: Trichy Junction is a major railway hub in South India.
By Road: Trichy is well-connected by National Highways. Our taxi service offers comfortable rides from Chennai, Madurai, and Tanjore.

**Getting Around Trichy**
While local buses are available, booking a taxi is the most comfortable way to see the sights. Our Trichy to Velankanni Taxi service offers reliable Airport pickup / Airport drop and local sightseeing packages.`,
  },
];

const BlogList = () => (
  <Layout>
    <img
      src="/trichy_rockfort_taxi_hero_1776155164304.png"
      alt="trichy travel blog guide for places to visit and trichy sightseeing"
      className="hidden"
    />
    <SEOHead
      title="Trichy Travel Blog - Tips, Guides & Routes | Trichy to Velankanni Taxi"
      description="Read our Trichy travel blog for tips, guides, route information, and travel inspiration. Plan your perfect trip to Trichy with expert insights."
      keywords="trichy travel blog, places to visit in trichy, trichy travel guide 2026, trichy to velankanni taxi, temple city tour trichy"
    />

    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          Travel <span className="text-gradient">Blog</span>
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto">
          Expert travel tips, guides, and inspiration for your Trichy journey.
        </motion.p>
      </div>
    </section>

    <section className="section-padding">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.article key={post.slug} {...fadeUp} transition={{ delay: i * 0.1 }}>
              <Link to={`/blog/${post.slug}`} className="glass-card-hover rounded-2xl overflow-hidden block group h-full">
                <div className="h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <Calendar className="w-16 h-16 text-primary/30" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                  </div>
                  <h2 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors mb-2">
                    {post.title}
                  </h2>
                  <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                  <span className="mt-4 inline-flex items-center text-sm text-primary font-medium">
                    Read More <ArrowRight className="w-4 h-4 ml-1 group-hover:ml-2 transition-all" />
                  </span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  </Layout>
);

export const blogPosts = posts;
export default BlogList;
